import { Row, Col, Form } from 'react-bootstrap';
import Common from '../../../shared/Common';
import { ClockIcon, CheckIcon, BanIcon, CircleNotchSpinIcon } from '../../../shared/icons/Svgs';
import type { IScraperSchedule } from '../../../../interfaces/IScraperSchedule';
import { type IScraperScheduleExecution, ScheduleStatus } from '../../../../interfaces/IScraperScheduleExecution';
import { useLocalStorage } from 'react-use';
import { useNavigate } from 'react-router-dom';

export interface IScheduleTableProps {
  schedules: IScraperSchedule[];
  loading: boolean;
  scheduleDate: Date;
  setScheduleDate: React.Dispatch<React.SetStateAction<Date>>;
}

const ScheduleTable = (props: IScheduleTableProps) => {
  const [showAllLocations, setShowAllLocations] = useLocalStorage(
    'ProductsScrapers.ScheduleTable.showAllLocations',
    false
  );
  const navigate = useNavigate();

  const generateScheduleExecutionsCell = (executions: IScraperScheduleExecution[]) => (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {executions.map((value, index) => {
        const calculateMargin = (time: Date, maxWidth: number) => {
          const minutes = time.getHours() * 60 + time.getMinutes();
          const totalMinutes = 24 * 60;
          return (minutes / totalMinutes) * maxWidth;
        };

        let isCloseToNext = true;
        if (index < executions.length - 1) {
          const nextValue = executions[index + 1];
          const diffInMinutes = Math.abs(new Date(value.time).getTime() - new Date(nextValue.time).getTime()) / 60000;
          isCloseToNext = diffInMinutes > 60;
        }

        return (
          <div
            key={index}
            style={{
              position: 'relative',
              cursor: 'text',
              left: calculateMargin(new Date(value.time), 800) - index * 16,
            }}
          >
            {generateCellValueForDate(value, isCloseToNext)}
          </div>
        );
      })}
    </div>
  );

  const generateCellValueForDate = (execution: IScraperScheduleExecution, showTime: boolean) => {
    const getResultIcon = (status: ScheduleStatus) => {
      switch (status) {
        case ScheduleStatus.Scheduled:
          return <ClockIcon style={{ width: '40px', height: '40px', marginTop: '-12px', marginBottom: '-12px' }} />;
        case ScheduleStatus.Completed:
          return <CheckIcon style={{ color: 'green' }} />;
        case ScheduleStatus.CustomErrorsOccured:
        case ScheduleStatus.NoRecordsCollected:
        case ScheduleStatus.DataFeedNotFound:
          return (
            <BanIcon
              style={{ color: 'red', height: '40px', width: '40px', marginTop: '-12px', marginBottom: '-12px' }}
            />
          );
        case ScheduleStatus.Running:
          return <CircleNotchSpinIcon style={{ color: '#007bff' }} />;
      }
    };
    const getResultTitle = (execution: IScraperScheduleExecution) => {
      switch (execution.status) {
        case ScheduleStatus.Scheduled:
          return 'Zaplanowano';
        case ScheduleStatus.Completed:
          return `Przetworzono ${execution.processedRecords} rekordów`;
        case ScheduleStatus.CustomErrorsOccured:
          return 'Nieprzewidziany błąd';
        case ScheduleStatus.NoRecordsCollected:
          return 'Nie pobrano żadnych rekordów';
        case ScheduleStatus.DataFeedNotFound:
          return 'Nie znaleziono próby wykonania';
        case ScheduleStatus.Running:
          return 'Aktualnie przetwarzane';
      }
    };
    const minutes =
      new Date(execution.time).getMinutes() > 9
        ? new Date(execution.time).getMinutes()
        : '0' + new Date(execution.time).getMinutes();
    const time = `${new Date(execution.time).getHours()}:${minutes}`;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }} title={getResultTitle(execution)}>
        <Row title={time} className={`justify-content-center initialism ${showTime ? '' : 'opacity-0'}`}>
          {time}
        </Row>
        <Row className='my-1 justify-content-center'>{getResultIcon(execution.status)}</Row>
      </div>
    );
  };

  const generateScheduleWithoutCronExecutionsCell = (
    executions: IScraperScheduleExecution[],
    numberOfDaysToShow: number,
    scheduleDate: Date
  ) => (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {executions.map((value, index) => {
        const calculateMargin = (time: Date, maxWidth: number, scheduleDate: Date) => {
          const endDate = scheduleDate;
          const startDate = new Date(scheduleDate);
          startDate.setDate(endDate.getDate() - numberOfDaysToShow);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          const totalMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
          const minutesFromStart = (time.getTime() - startDate.getTime()) / 60000;

          return (minutesFromStart / totalMinutes) * maxWidth;
        };

        let isCloseToNext = true;
        if (index < executions.length - 1) {
          const nextValue = executions[index + 1];
          const diffInMinutes = Math.abs(new Date(value.time).getTime() - new Date(nextValue.time).getTime()) / 60000;
          isCloseToNext = diffInMinutes > numberOfDaysToShow * 65;
        }

        return (
          <div
            key={index}
            style={{
              position: 'relative',
              cursor: 'text',
              left: calculateMargin(new Date(value.time), 780, scheduleDate) - index * 16,
            }}
          >
            {generateCellValueForDateWithoutCron(value, isCloseToNext)}
          </div>
        );
      })}
    </div>
  );

  const generateCellValueForDateWithoutCron = (execution: IScraperScheduleExecution, showTime: boolean) => {
    const getResultIcon = (status: ScheduleStatus) => {
      switch (status) {
        case ScheduleStatus.Scheduled:
          return <ClockIcon style={{ width: '40px', height: '40px', marginTop: '-12px', marginBottom: '-12px' }} />;
        case ScheduleStatus.Completed:
          return <CheckIcon style={{ color: 'green' }} />;
        case ScheduleStatus.CustomErrorsOccured:
        case ScheduleStatus.NoRecordsCollected:
        case ScheduleStatus.DataFeedNotFound:
          return (
            <BanIcon
              style={{ color: 'red', height: '40px', width: '40px', marginTop: '-12px', marginBottom: '-12px' }}
            />
          );
        case ScheduleStatus.Running:
          return <CircleNotchSpinIcon style={{ color: '#007bff' }} />;
      }
    };
    const getResultTitle = (execution: IScraperScheduleExecution) => {
      const timeString = Common.Utils.getDateWithTimeWithoutZoneConverter(new Date(execution.time));
      switch (execution.status) {
        case ScheduleStatus.Scheduled:
          return `Zaplanowano - ${timeString}`;
        case ScheduleStatus.Completed:
          return `Przetworzono ${execution.processedRecords} rekordów - ${timeString}`;
        case ScheduleStatus.CustomErrorsOccured:
          return `Nieprzewidziany błąd - ${timeString}`;
        case ScheduleStatus.NoRecordsCollected:
          return `Nie pobrano żadnych rekordów - ${timeString}`;
        case ScheduleStatus.DataFeedNotFound:
          return `Nie znaleziono próby wykonania - ${timeString}`;
        case ScheduleStatus.Running:
          return `Aktualnie przetwarzane - ${timeString}`;
      }
    };
    const minutes =
      new Date(execution.time).getMinutes() > 9
        ? new Date(execution.time).getMinutes()
        : '0' + new Date(execution.time).getMinutes();
    const time = `${new Date(execution.time).getHours()}:${minutes}`;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }} title={getResultTitle(execution)}>
        <Row title={time} className={`justify-content-center initialism ${showTime ? '' : 'opacity-0'}`}>
          {time}
        </Row>
        <Row className='my-1 justify-content-center'>{getResultIcon(execution.status)}</Row>
      </div>
    );
  };

  const schedules = props.schedules.reduce((acc: IScraperSchedule[], current) => {
    const elem = acc.find((item: IScraperSchedule) => item.companyId === current.companyId);
    if (showAllLocations || !elem) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, []);

  return (
    <>
      <h6 className='mt-3'>Harmonogramy wykonań</h6>
      {!props.loading && (
        <Row>
          <Col sm='auto'>
            <Form.Control
              style={{ width: 200 }}
              type='date'
              name='schedules'
              placeholder='Dzień wykonania'
              onChange={(e: any) => {
                props.setScheduleDate(new Date(e.target.value));
              }}
              defaultValue={props.scheduleDate.toLocaleString('en-CA').split(',')[0]}
            />
          </Col>
          <Col>
            <Form.Check
              className='my-2'
              type='switch'
              id='show-all-locations-switch'
              label='Pokaż wszystkie lokalizacje'
              style={{ userSelect: 'none' }}
              checked={showAllLocations}
              onChange={(e: any) => {
                setShowAllLocations(e.target.checked);
              }}
            />
          </Col>
        </Row>
      )}
      <div className='mt-2 table-responsive'>
        <table className='table table-hover table-sm' style={{ width: '960px' }}>
          <tbody>
            {props.loading
              ? Common.Ui.getSkeletonTableRows(8, 1, 35)
              : schedules
                  .filter((value) => value.useCronSchedule)
                  .map((value, index) => (
                    <tr
                      key={index}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/settings/application/products-scrapers/' + value.companyLocationId)}
                    >
                      <td style={{ width: '140px' }}>{value.displayName}</td>
                      <td style={{ width: '800px' }}>{generateScheduleExecutionsCell(value.executions)}</td>
                    </tr>
                  ))}
          </tbody>
        </table>
      </div>
      <h6 className='mt-3'>Wykonania bez harmonogramu (z ostatnich 7 dni)</h6>
      <div className='mt-2 table-responsive'>
        <table className='table table-hover table-sm' style={{ width: '960px' }}>
          <tbody>
            {props.loading
              ? Common.Ui.getSkeletonTableRows(8, 1, 35)
              : schedules
                  .filter((value) => !value.useCronSchedule)
                  .map((value, index) => (
                    <tr
                      key={index}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/settings/application/products-scrapers/' + value.companyLocationId)}
                    >
                      <td style={{ width: '140px' }}>{value.displayName}</td>
                      <td style={{ width: '800px' }}>
                        {generateScheduleWithoutCronExecutionsCell(value.executions, 7, props.scheduleDate)}
                      </td>
                    </tr>
                  ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ScheduleTable;
